export enum WorkPriority {
  Low = 0,
  Medium = 1,
  High = 2,
  // Reserved for interactive requests (a user is awaiting this item), so they
  // front-run any queued background work.
  User = 3,
}

export type WorkResult<TOutput> =
  { ok: true; value: TOutput } | { ok: false; error: Error };

interface WorkItem<T> {
  id: string;
  data: T;
  priority: WorkPriority;
  addedAt: number;
  retries: number;
}

interface WorkQueueOptions<TInput, TOutput> {
  name: string;
  concurrency: number;
  /** Items are batched only with same-group items; the head item's group wins. */
  batchGroup?: (item: TInput) => string;
  /** Max batch size for the head item's group. */
  batchSizeFor?: (item: TInput) => number;
  processingDelay?: number;
  /** Per ITEM, not per batch. */
  maxRetries?: number;
  getId: (item: TInput) => string;
  /** Aligned with items. Throwing = every item in the batch failed. */
  worker: (items: TInput[]) => Promise<WorkResult<TOutput>[]>;
  /** Called once per batch with SETTLED items only (successes + final
   *  failures). Retryable failures are re-enqueued silently. */
  onComplete: (
    results: WorkResult<TOutput>[],
    inputs: TInput[]
  ) => Promise<void>;
  onProcessingChanged?: (processing: boolean) => void;
}

export class WorkQueue<TInput, TOutput> {
  private itemsById = new Map<string, WorkItem<TInput>>();
  private activeWorkers = 0;
  private generation = 0;
  private options: Required<WorkQueueOptions<TInput, TOutput>>;

  constructor(options: WorkQueueOptions<TInput, TOutput>) {
    this.options = {
      batchGroup: () => "",
      batchSizeFor: () => 1,
      processingDelay: 100,
      maxRetries: 3,
      onProcessingChanged: (_processing: boolean) => {},
      ...options,
    };
  }

  enqueue(items: TInput[], priority: WorkPriority = WorkPriority.Medium) {
    const now = Date.now();

    for (const item of items) {
      const id = this.options.getId(item);
      const existing = this.itemsById.get(id);

      if (existing) {
        // Update priority if higher
        if (priority > existing.priority) {
          existing.priority = priority;
        }
      } else {
        // Add new item
        this.itemsById.set(id, {
          id,
          data: item,
          priority,
          addedAt: now,
          retries: 0,
        });
      }
    }
    void this.startProcessing();
  }

  private startProcessing() {
    // Start new workers up to concurrency limit
    while (
      this.activeWorkers < this.options.concurrency &&
      this.itemsById.size > 0
    ) {
      this.activeWorkers++;

      // The first worker to start triggers the processing changed callback
      if (this.activeWorkers === 1) {
        try {
          this.options.onProcessingChanged(true);
        } catch (error) {
          console.error("onProcessingChanged callback error:", error);
        }
      }

      // Run the worker
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.runWorker();
    }
  }

  private async runWorker() {
    try {
      while (this.itemsById.size > 0) {
        const generation = this.generation;
        // Get next batch (sorted by priority, then age) and remove from queue immediately
        const batch = this.claimNextBatch();

        // No work, audi 5000
        if (batch.length === 0) {
          break;
        }

        const inputs = batch.map((item) => item.data);

        let results: WorkResult<TOutput>[];
        try {
          results = await this.options.worker(inputs);
        } catch (error) {
          // A thrown worker fails every item in the batch identically; each
          // still gets its own retry decision below.
          const err = error instanceof Error ? error : new Error(String(error));
          results = inputs.map(() => ({ ok: false, error: err }));
        }

        if (generation !== this.generation) {
          continue;
        }

        const settledResults: WorkResult<TOutput>[] = [];
        const settledInputs: TInput[] = [];
        for (const [i, item] of batch.entries()) {
          const result = results[i] ?? {
            ok: false,
            error: new Error("Worker returned no result for item"),
          };
          if (!result.ok && item.retries < this.options.maxRetries) {
            item.retries++;
            this.itemsById.set(item.id, item);
          } else {
            settledResults.push(result);
            settledInputs.push(item.data);
          }
        }
        if (settledResults.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.options.onComplete(settledResults, settledInputs);
        }

        // Delay between batches
        if (this.itemsById.size > 0 && this.options.processingDelay > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.options.processingDelay)
          );
        }
      }
    } finally {
      // This worker is stopping
      this.activeWorkers--;

      if (this.activeWorkers === 0) {
        try {
          this.options.onProcessingChanged(false);
        } catch (error) {
          console.error("onProcessingChanged callback error:", error);
        }
      }
    }
  }

  private claimNextBatch(): WorkItem<TInput>[] {
    // Fetch the highest priority, oldest items
    const items = Array.from(this.itemsById.values()).sort((a, b) => {
      if (a.priority !== b.priority) {
        // Higher priority first
        return b.priority - a.priority;
      }
      // Older first
      return a.addedAt - b.addedAt;
    });

    const head = items[0];
    if (!head) {
      return [];
    }

    // Extend the head with same-group items (in priority/age order), up to
    // the head's group's batch size — heterogeneous kinds share the queue
    // without sharing a batch.
    const group = this.options.batchGroup(head.data);
    const maxSize = this.options.batchSizeFor(head.data);
    const batch: WorkItem<TInput>[] = [];
    for (const item of items) {
      if (batch.length >= maxSize) {
        break;
      }
      if (this.options.batchGroup(item.data) === group) {
        batch.push(item);
      }
    }

    // Remove claimed items from the queue immediately
    batch.forEach((item) => this.itemsById.delete(item.id));
    return batch;
  }

  /**
   * Drop all queued work AND invalidate in-flight batches: their results
   * are discarded without retry or onComplete.
   */
  clear() {
    this.generation++;
    this.itemsById.clear();
  }

  removeByIds(ids: string[]) {
    for (const id of ids) {
      this.itemsById.delete(id);
    }
  }

  get size() {
    return this.itemsById.size;
  }

  get isProcessing() {
    return this.activeWorkers > 0;
  }
}
